import { log } from 'log';

import { POSTCODE_PROVIDER_ERROR } from './constants';
import * as Types from './types';

export const parseResponseError = (error: Types.IPostCodeError) => {
    return {
        errorCode: error.Error,
        errorMessage: 'playtech.mobile.system.form.addressLookup.serviceError.default'
    };
};

export const createParsingError = (error: any) => {
    if (error.errorCode !== undefined) {
        return error;
    }

    return {
        ...error,
        errorCode: POSTCODE_PROVIDER_ERROR.PARSE_ERROR,
        errorMessage: 'playtech.mobile.system.form.addressLookup.serviceError.default'
    };
};

export const createFetchError = (error: any) => {
    if (error.errorCode !== undefined) {
        return error;
    }

    if (error.name === 'AbortError') {
        return { errorCode: POSTCODE_PROVIDER_ERROR.ABORT_ERROR };
    }

    return {
        ...error,
        errorCode: POSTCODE_PROVIDER_ERROR.NETWORK_ERROR,
        errorMessage: 'playtech.mobile.system.form.addressLookup.serviceError.networkError'
    };
};

export const get = async (url: string, signal: AbortSignal): Promise<Response> => {
    try {
        log('httpReq', { url, method: 'GET', time: new Date() });

        return await fetch(url, {
            method: 'GET',
            signal
        });
    } catch (error) {
        throw createFetchError(error);
    }
};

export const parseResponse = async (response: Response): Promise<(Types.IPostCodeSearchItem | Types.IPostCodeRetrieveItem)[]> => {
    const data = await response.json() as Types.TPostCodeResponseData;

    log('httpRes', { data, url: response.url });

    const rawItems = data.Items;
    const firstItem = rawItems[0];

    if (rawItems.length === 1 && 'Error' in firstItem) {
        throw parseResponseError(firstItem);
    }

    return rawItems as (Types.IPostCodeSearchItem | Types.IPostCodeRetrieveItem)[];
};

export const parseFind = async (response: Response): Promise<Types.IParsedSearchItem[]> => {
    try {
        const searchItems = await parseResponse(response) as Types.IPostCodeSearchItem[];

        return searchItems.map(item => ({
            id: item.Id,
            type: item.Type,
            text: item.Text,
            description: item.Description,
            highlight: item.Highlight
        }));
    } catch (error) {
        throw createParsingError(error);
    }
};

export const parseRetrieve = async (response: Response): Promise<Types.IParsedRetrieveItem[]> => {
    try {
        const retrieveItems = await parseResponse(response) as Types.IPostCodeRetrieveItem[];

        return retrieveItems.map(item => ({
            id: item.Id,
            label: item.Label,
            state: item.Province,
            province: item.Province,
            city: item.City,
            address: item.Line1,
            address2: item.Line2,
            zip: item.PostalCode,
            streetName: item.Street,
            buildingNumber: item.BuildingNumber,
            propertyNumber: item.BuildingNumber,
            subbuilding: item.SubBuilding,
            unitNumber: item.SubBuilding,
            isRetrievedAddress: true
        }));
    } catch (error) {
        throw createParsingError(error);
    }
};

export const searchResultsToOptions = (searchResults: Types.IParsedSearchItem[]): {
    value: string;
    label: string;
    data: Types.IParsedSearchItem;
}[] => {
    return searchResults.map((item, index) => ({
        value: `${item.id}_${index}`,
        label: item.description,
        data: item
    }));
};

export const retrieveResultsToOptions = (retrieveResults: Types.IParsedRetrieveItem[]): {
    value: string;
    label: string;
    data: Types.IParsedRetrieveItem & {
        text: string;
        description: string;
        highlight: string;
    };
}[] => {
    return retrieveResults.map((item, index) => ({
        value: `${item.id}_${index}`,
        label: `${item.address} ${item.zip} ${item.city}`,
        data: {
            text: `${item.address} ${item.zip} ${item.city}`,
            description: '',
            highlight: '',
            ...item
        }
    }));
};
